import Building, {IBuilding} from "@/types/clients/esquire/data/general/building";
import Contact, {IContact} from "@/types/clients/esquire/data/general/contact";
import Contract, {IContractData} from "@/types/clients/esquire/data/general/contract";
import _ from "lodash";
import PendingPayment, {IPendingPayment} from "@/types/clients/esquire/data/general/pending-payment";

import DateService from "@/services/date-service";
import MoneyService from "@/services/money-service";

export interface IReservation {
  id: string;
  channel_id: string;
  checkin_performed_at: null|string;
  building: IBuilding;
  contact: IContact;
  contracts: IContractData[];
  pending_payment: null|IPendingPayment;
}

type ReservationPersist = IReservation & {
  routes: Record<string, string>;
};

export default class Reservation {
  public id: string;
  public channelId: string;
  public checkinPerformedAt: null|string;
  public building: Building;
  public contact: Contact;
  public contracts: Contract[];
  public pendingPayment: null|PendingPayment;
  public routes: Record<string, string> = {};

  private readonly _minArrival: string;
  private readonly _maxDeparture: string;

  private readonly _nights: number;

  private readonly _accruedDebt: number;
  private readonly _accruedDebtToday: number;
  private readonly _paid: number;

  constructor(data: IReservation) {
    this.id = data.id;
    this.channelId = data.channel_id;
    this.checkinPerformedAt = data.checkin_performed_at;
    this.building = new Building(data.building as IBuilding);
    this.contact = new Contact(data.contact as IContact);
    this.contracts = data.contracts.map(data => new Contract(data));

    this.pendingPayment = data.pending_payment ? new PendingPayment(data.pending_payment as IPendingPayment) : null;

    this._maxDeparture = this.getMaxDeparture();
    this._minArrival = this.getMinArrival();

    this._nights = this.getNights();

    this._accruedDebt = this.calculateAccruedDebt();
    this._accruedDebtToday = this.calculateAccruedDebtToday();
    this._paid = this.calculatePaid();
  }

  /** Pure: find a contract by id on this reservation. */
  public getContractById(contractId: string | null | undefined): Contract | null {
    if (!contractId) return null;
    return this.contracts.find(c => c.id === contractId) ?? null;
  }

  get accruedDebt(): number {
    return this._accruedDebt;
  }

  get hasCancelledContract(): boolean {
    return this.contracts.some(contract => contract.cancelledAt !== null);
  }

  get accruedDebtToday(): number {
    return this._accruedDebtToday;
  }

  get maxDeparture(): string {
    return this._maxDeparture;
  }

  get minArrival(): string {
    return this._minArrival;
  }


  get formattedMaxDeparture(): string {
    return DateService.formatDate(this._maxDeparture);
  }

  get formattedMinArrival(): string {
    return DateService.formatDate(this._minArrival);
  }

  get nights(): number {
    return this._nights;
  }

  get paid(): number {
    return this._paid;
  }

  public calculateAccruedDebt(): number {
    return _.sumBy(this.contracts, (c: Contract) => c.accruedDebt);
  }

  public calculateAccruedDebtToday(): number {
    return _.sumBy(this.contracts, (c: Contract) => c.accruedDebtToday);
  }

  public calculatePaid(): number {
    return _.sumBy(this.contracts, (c: Contract) => c.paid);
  }

  public calculateToPay(): number {
    return MoneyService.payable(this.accruedDebt - this.paid + this.getOptionPriceTotal());
  }

  public calculateExtraOptionsByNights(extraNights: number): number {
    return _.sumBy(this.contracts, (c: Contract) => c.getOptionPricePerAmountOfNights(extraNights));
  }

  public calculateToPayToday(): number {
    return MoneyService.payable(
      this.accruedDebtToday - this.paid + _.sumBy(this.contracts, (c: Contract) => c.getOptionPricePerMonth())
    );
  }

  public getOptionPriceTotal(): number {
    return _.sumBy(this.contracts, (c: Contract) => c.getOptionPriceTotal());
  }

  public getBalanceTodayAfterExtension(extraNights: number, extensionPrice: number): number {
    return MoneyService.round(
      this.accruedDebtToday - this.paid + this.calculateExtraOptionsByNights(extraNights) + extensionPrice
    );
  }

  private getMinArrival(): string {
    return _.chain(this.contracts)
      .map((contract: Contract) => contract.startDate)
      .min()
      .value();
  }

  private getMaxDeparture(): string {
    return _.chain(this.contracts)
      .map((contract: Contract) => contract.endDate)
      .max()
      .value();
  }

  private getNights(): number {
    return DateService.diffDays(this.maxDeparture, this.minArrival);
  }

  toDTO(): IReservation {
    return {
      id: this.id,
      channel_id: this.channelId,
      checkin_performed_at: this.checkinPerformedAt,
      building: this.building.toDTO(),
      contact: this.contact.toDTO(),
      contracts: this.contracts.map(c => c.toDTO()),
      pending_payment: this.pendingPayment ? this.pendingPayment.toDTO() : null
    };
  }

  toPersist(): ReservationPersist {
    return {
      ...this.toDTO(),
      routes: this.routes ?? {}
    };
  }

  static fromDTO(dto: IReservation): Reservation {
    return new Reservation(dto);
  }

  static fromPersist(p: ReservationPersist): Reservation {
    const r = new Reservation(p);
    r.routes = p.routes ?? {};
    return r;
  }



}
